import React, { useEffect, useMemo, useState } from 'react';

const httpMethods = new Set(['get', 'post', 'put', 'patch', 'delete', 'options', 'head']);

function collectOperations(openapi) {
  const operations = [];
  Object.entries(openapi?.paths ?? {}).forEach(([routePath, pathItem]) => {
    Object.entries(pathItem ?? {}).forEach(([method, operation]) => {
      if (!httpMethods.has(method)) return;
      const tags = operation.tags?.length ? operation.tags : ['Untagged'];
      operations.push({
        id: operation.operationId ?? `${method}-${routePath}`,
        method: method.toUpperCase(),
        path: routePath,
        summary: operation.summary ?? operation.description ?? routePath,
        description: operation.description,
        tags
      });
    });
  });
  return operations.sort((a, b) => `${a.tags[0]} ${a.path}`.localeCompare(`${b.tags[0]} ${b.path}`));
}

const ApiReference = () => {
  const [openapi, setOpenapi] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadOpenApi() {
      try {
        const response = await fetch('/generated/openapi.json');
        if (!response.ok) {
          throw new Error(`OpenAPI-artefakt mangler (${response.status})`);
        }
        const payload = await response.json();
        if (mounted) setOpenapi(payload);
      } catch (loadError) {
        if (mounted) setError(loadError);
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    loadOpenApi();
    return () => {
      mounted = false;
    };
  }, []);

  const operationsByTag = useMemo(() => {
    const grouped = new Map();
    collectOperations(openapi).forEach((operation) => {
      operation.tags.forEach((tag) => {
        const operations = grouped.get(tag) ?? [];
        operations.push(operation);
        grouped.set(tag, operations);
      });
    });
    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [openapi]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary-600">API</p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">OpenAPI reference</h1>
          <p className="mt-3 max-w-3xl text-gray-600">
            Generert API-referanse synket fra `promang-api` når OpenAPI-artefaktet finnes.
          </p>
        </div>

        {isLoading && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-600">
            Laster OpenAPI...
          </div>
        )}

        {!isLoading && error && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900">
            <h2 className="text-lg font-semibold">OpenAPI er ikke tilgjengelig i portalbundle ennå</h2>
            <p className="mt-2">
              Kjør `npm run docs:sync-openapi` etter at API-repoet har publisert
              `public/docs/generated/openapi.json`.
            </p>
            <p className="mt-2 text-sm">{error.message}</p>
          </div>
        )}

        {!isLoading && openapi && (
          <div className="space-y-8">
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {openapi.info?.title ?? 'Promang API'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">Versjon {openapi.info?.version ?? 'ukjent'}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 font-semibold text-indigo-700">
                  OpenAPI-level: {openapi['x-promang-openapi-level'] ?? 'bootstrap'}
                </span>
                {openapi.components?.securitySchemes?.bearerAuth && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 font-semibold text-emerald-700">
                    bearerAuth (Doorkeeper)
                  </span>
                )}
              </div>
              {openapi.info?.description && (
                <p className="mt-3 text-gray-600">{openapi.info.description}</p>
              )}
            </div>

            {operationsByTag.map(([tag, operations]) => (
              <section key={tag} className="rounded-lg border border-gray-200 bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">{tag}</h2>
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-600">
                    {operations.length} operasjoner
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {operations.map((operation) => (
                    <article key={`${tag}-${operation.id}`} className="py-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="rounded bg-primary-100 px-2 py-1 font-mono text-xs font-semibold text-primary-700">
                          {operation.method}
                        </span>
                        <code className="text-sm text-gray-900">{operation.path}</code>
                      </div>
                      <h3 className="mt-2 text-base font-medium text-gray-900">{operation.summary}</h3>
                      {operation.description && (
                        <p className="mt-1 text-sm text-gray-600">{operation.description}</p>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiReference;
